import { AUTOCOMPLETE_DEBOUNCE_MS } from "@racler/shared";
import type { AutocompleteRequest, AutocompleteResponse } from "@racler/protocol";
import { monaco, languageForPath } from "../monaco.ts";
import { loadEditorSettings } from "../editorSettings.ts";
import type { RelayClient } from "../relayClient.ts";
import {
  AutocompleteController,
  extractBoundedContext,
  isPermanentUnavailableReason,
  isResponseValid,
  parseModelUri,
  setAutocompleteStatus,
  shouldTrigger,
} from "./autocompleteLogic.ts";

export * from "./autocompleteLogic.ts";

let registeredProvider: monaco.IDisposable | null = null;
let controller: AutocompleteController | null = null;

function sleepWithToken(ms: number, token: monaco.CancellationToken): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    if (token.isCancellationRequested) return resolve(false);
    const timer = setTimeout(() => resolve(true), ms);
    const sub = token.onCancellationRequested(() => {
      clearTimeout(timer);
      sub.dispose();
      resolve(false);
    });
  });
}

/**
 * Registers Monaco inline completions provider for Racler files.
 * Idempotent: can be called multiple times safely.
 */
export function registerAutocompleteProvider(relay: RelayClient): monaco.IDisposable {
  if (registeredProvider) {
    return registeredProvider;
  }
  controller = new AutocompleteController(relay);

  const provider: monaco.languages.InlineCompletionsProvider = {
    async provideInlineCompletions(model, position, context, token) {
      const settings = loadEditorSettings();
      if (!shouldTrigger(settings.autocomplete, context.selectedSuggestionInfo !== undefined, model.isDisposed())) {
        return { items: [] };
      }

      const parsedUri = parseModelUri(model.uri);
      if (!parsedUri) {
        return { items: [] };
      }

      const waited = await sleepWithToken(AUTOCOMPLETE_DEBOUNCE_MS, token);
      if (!waited || token.isCancellationRequested || model.isDisposed()) {
        return { items: [] };
      }

      controller!.setMachineId(parsedUri.machineId);
      const { controller: reqAbort, requestId } = controller!.startRequest();

      const cancelSub = token.onCancellationRequested(() => {
        controller!.cancelActive();
        cancelSub.dispose();
      });

      const capturedVersionId = model.getVersionId();
      const pos = position;
      const fullPrefix = model.getValueInRange(new monaco.Range(1, 1, pos.lineNumber, pos.column));
      const totalLines = model.getLineCount();
      const maxCol = model.getLineMaxColumn(totalLines);
      const fullSuffix = model.getValueInRange(new monaco.Range(pos.lineNumber, pos.column, totalLines, maxCol));
      const { prefix, suffix } = extractBoundedContext(fullPrefix, fullSuffix);

      const req: AutocompleteRequest = {
        requestId,
        projectId: parsedUri.projectId,
        path: parsedUri.path,
        language: languageForPath(parsedUri.path),
        modelVersion: capturedVersionId,
        cursorPosition: {
          lineNumber: pos.lineNumber,
          column: pos.column,
        },
        prefix,
        suffix,
      };

      try {
        const res: AutocompleteResponse = await relay.autocomplete(
          parsedUri.machineId,
          req,
          reqAbort.signal,
        );

        if (!isResponseValid(capturedVersionId, model.getVersionId(), token.isCancellationRequested, res)) {
          if (res?.unavailable && isPermanentUnavailableReason(res.reason)) {
            setAutocompleteStatus({ unavailableReason: res.reason ?? "Autocomplete unavailable" });
          }
          return { items: [] };
        }

        setAutocompleteStatus({ unavailableReason: null });

        return {
          items: [
            {
              insertText: res.text,
              range: new monaco.Range(pos.lineNumber, pos.column, pos.lineNumber, pos.column),
            },
          ],
        };
      } catch {
        // Transient error (network failure, timeout, temporary API error, etc.):
        // Return no suggestion for that request, keep autocomplete enabled, keep UI state "AI On".
        // Never convert a single transient request failure into persistent unavailable capability state.
        return { items: [] };
      } finally {
        cancelSub.dispose();
        controller!.cleanup(requestId);
      }
    },
    disposeInlineCompletions() {},
  };

  registeredProvider = monaco.languages.registerInlineCompletionsProvider(
    { scheme: "racler" },
    provider,
  );
  return registeredProvider;
}
